(() => {
  const normalize = (value) => value.toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const REQUIRED_MESSAGE = 'Pick a breed design before adding to cart.';

  function errorEl(breedInput) {
    const root = breedInput.closest('.__input-wrapper--breed-gallery') || breedInput.parentElement;
    return (root && root.querySelector('[data-lylaloo-breed-required-error]')) || null;
  }

  function framedSource() {
    return document.querySelector('[data-lylaloo-framed-art-source]');
  }

  function galleryPathRequired() {
    const source = framedSource();
    if (!source) return true;
    return source.value === 'Gallery design';
  }

  function isEmpty(breedInput) {
    return !String(breedInput.value || '').trim();
  }

  function setEmptyState(breedInput, empty) {
    const error = errorEl(breedInput);
    if (empty) {
      breedInput.setCustomValidity(REQUIRED_MESSAGE);
      breedInput.classList.add('is-invalid');
      if (error) error.hidden = false;
    } else {
      breedInput.setCustomValidity('');
      breedInput.classList.remove('is-invalid');
      if (error) error.hidden = true;
    }
  }

  function lockTyping(breedInput) {
    breedInput.addEventListener('keydown', (event) => {
      if (event.key === 'Tab' || event.metaKey || event.ctrlKey || event.altKey) return;
      event.preventDefault();
    });
    breedInput.addEventListener('paste', (event) => event.preventDefault());
    breedInput.addEventListener('drop', (event) => event.preventDefault());
    breedInput.addEventListener('beforeinput', (event) => {
      if (event.inputType && event.inputType.startsWith('insert')) event.preventDefault();
    });
  }

  function bindRequiredGuard(breedInput) {
    if (breedInput.dataset.lylalooBreedGuard === '1') return;
    breedInput.dataset.lylalooBreedGuard = '1';
    lockTyping(breedInput);
    if (galleryPathRequired()) {
      setEmptyState(breedInput, isEmpty(breedInput));
    } else {
      setEmptyState(breedInput, false);
    }

    const formId = breedInput.getAttribute('form');
    const form = (formId && document.getElementById(formId)) || breedInput.closest('form');
    const blockIfEmpty = (event) => {
      if (!galleryPathRequired()) return;
      if (!isEmpty(breedInput)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setEmptyState(breedInput, true);
      breedInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try {
        breedInput.reportValidity();
      } catch (err) {
        /* ignore browsers that skip reportValidity */
      }
      breedInput.focus({ preventScroll: true });
    };

    const source = framedSource();
    if (source) {
      source.addEventListener('change', () => {
        if (galleryPathRequired()) {
          setEmptyState(breedInput, isEmpty(breedInput));
        } else {
          setEmptyState(breedInput, false);
        }
      });
    }

    if (form) {
      form.addEventListener('submit', blockIfEmpty, true);
      form.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (!target.closest('add-to-cart-component, [name="add"], button[type="submit"]')) return;
        blockIfEmpty(event);
      }, true);
    }
  }

  function initializeGallery(root) {
    const designs = Array.isArray(window.LylaLooBreedGallery) ? window.LylaLooBreedGallery : [];
    const grid = root.querySelector('[data-gallery-grid]');
    const search = root.querySelector('[data-gallery-search]');
    const count = root.querySelector('[data-gallery-count]');
    const selectedPanel = root.querySelector('[data-gallery-selected]');
    const selectedText = root.querySelector('[data-gallery-selected-text]');
    const returnLink = root.querySelector('[data-gallery-return]');
    const breedInput = document.querySelector('[data-lylaloo-breed-gallery-input]');

    if (breedInput) bindRequiredGuard(breedInput);
    if (!grid || !search || !count || !breedInput || designs.length === 0) return;

    const spriteUrls = {
      'lylaloo-breed-gallery-1.webp': root.getAttribute('data-sprite-1') || root.dataset.sprite1,
      'lylaloo-breed-gallery-2.webp': root.getAttribute('data-sprite-2') || root.dataset.sprite2,
      'lylaloo-breed-gallery-3.webp': root.getAttribute('data-sprite-3') || root.dataset.sprite3,
    };
    let selectedValue = breedInput.value;

    function selectDesign(design, card) {
      selectedValue = design.value;
      breedInput.value = design.value;
      setEmptyState(breedInput, false);
      breedInput.dispatchEvent(new Event('input', { bubbles: true }));
      breedInput.dispatchEvent(new Event('change', { bubbles: true }));
      grid.querySelectorAll('[aria-pressed="true"]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
      card.setAttribute('aria-pressed', 'true');
      selectedText.textContent = design.breed + ' (' + design.code + ')';
      selectedPanel.hidden = false;
      returnLink.href = '#lylaloo-product-options';
      selectedPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function render(filter) {
      const term = normalize((filter || '').trim());
      const visible = designs.filter((design) => {
        if (!term) return true;
        return normalize(design.breed + ' ' + design.code).includes(term);
      });
      const fragment = document.createDocumentFragment();

      visible.forEach((design) => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = 'lylaloo-breed-gallery__card';
        card.setAttribute('aria-pressed', selectedValue === design.value ? 'true' : 'false');
        card.setAttribute('aria-label', 'Choose ' + design.breed + ', ' + design.code + ' design');

        const preview = document.createElement('span');
        preview.className = 'lylaloo-breed-gallery__preview';
        preview.setAttribute('aria-hidden', 'true');
        const spriteUrl = spriteUrls[design.sprite] || '';
        preview.style.backgroundImage = 'url("' + spriteUrl + '")';
        preview.style.backgroundSize = (design.columns * 100) + '% ' + (design.rows * 100) + '%';
        const x = design.columns > 1 ? (design.column * 100) / (design.columns - 1) : 0;
        const y = design.rows > 1 ? (design.row * 100) / (design.rows - 1) : 0;
        preview.style.backgroundPosition = x + '% ' + y + '%';

        const label = document.createElement('span');
        label.className = 'lylaloo-breed-gallery__label';
        label.textContent = design.breed;

        const code = document.createElement('span');
        code.className = 'lylaloo-breed-gallery__code';
        code.textContent = design.code;

        card.append(preview, label, code);
        card.addEventListener('click', () => selectDesign(design, card));
        fragment.append(card);
      });

      grid.replaceChildren(fragment);
      count.textContent = visible.length + (visible.length === 1 ? ' design shown' : ' designs shown');
    }

    search.addEventListener('input', () => render(search.value));
    render('');
  }

  function start() {
    document.querySelectorAll('[data-lylaloo-breed-gallery-input]').forEach(bindRequiredGuard);
    document.querySelectorAll('[data-breed-gallery]').forEach(initializeGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
